import React, { useState, useEffect } from 'react';
import api from '../../lib/api';

const PORTAL_IMAGE = 'https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto,w_800/v1774891328/Acceso_campus_definitivo_cwro5v.png';

const CampusUmbral = ({ onComplete }) => {
  const [question, setQuestion] = useState(null);
  const [response, setResponse] = useState('');
  const [entering, setEntering] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchQuestion();
  }, []);

  const fetchQuestion = async () => {
    try {
      const res = await api.get('/campus/sacred-question');
      setQuestion(res.data);
    } catch {
      setQuestion({ question: '¿Cómo llega tu respiración a tu cuerpo en este momento?', id: 'default' });
    }
  };

  const handleSubmit = async () => {
    if (!response.trim()) return;
    setSaving(true);
    try {
      await api.post('/campus/bitacora', {
        question: question?.question || '',
        response: response.trim(),
        question_id: question?.id || '',
      });
    } catch { /* Still allow entry */ }
    setEntering(true);
    setTimeout(() => onComplete(), 1800);
  };

  const handleSkip = () => {
    setEntering(true);
    setTimeout(() => onComplete(), 1400);
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="campus-umbral">
      <style>{`
        @keyframes fadeInUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes breatheDot{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:.8;transform:scale(1.2)}}
        @keyframes portalBreathe{0%,100%{opacity:.06;transform:scale(.95)}50%{opacity:.18;transform:scale(1.05)}}
        @keyframes dustRise{0%{opacity:0;transform:translateY(0) scale(.3)}10%{opacity:.6}60%{opacity:.2}100%{opacity:0;transform:translateY(-45vh) scale(.1)}}
        @keyframes chevronPulse{0%,100%{opacity:.25;transform:translateY(0)}50%{opacity:.6;transform:translateY(6px)}}
      `}</style>

      {/* White flash overlay when entering */}
      <div className={`fixed inset-0 bg-white z-50 pointer-events-none transition-opacity duration-[1500ms] ${
        entering ? 'opacity-100' : 'opacity-0'
      }`} />

      {/* ═══ HERO: TITLE + PORTAL — FITS IN VIEWPORT ═══ */}
      <div className="h-screen flex flex-col">

        {/* Title bar at top */}
        <div className="w-full bg-[#5c4a2a] py-3 shrink-0">
          <p className="text-center text-[#c9a227] text-xs tracking-[0.4em] uppercase font-light" data-testid="umbral-header">
            Campus de Interioridad
          </p>
        </div>

        {/* Portal image — transparent PNG on cream background */}
        <div className="flex-1 flex items-center justify-center overflow-hidden relative">
          <img
            src={PORTAL_IMAGE}
            alt="Portal Sagrado"
            className="block cursor-pointer"
            style={{
              maxHeight: 'calc(100vh - 52px)',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
            draggable={false}
            onClick={handleSkip}
            data-testid="umbral-portal-image"
          />

          {/* SVG magical effects overlay */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <svg
              viewBox="0 0 100 100"
              className="block"
              style={{ maxHeight: 'calc(100vh - 52px)', maxWidth: '100%', aspectRatio: '1/1' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <radialGradient id="portalLight" cx="50%" cy="52%" r="18%">
                  <stop offset="0%" stopColor="#f5e6a3" stopOpacity="1"/>
                  <stop offset="60%" stopColor="#f5e6a3" stopOpacity=".3"/>
                  <stop offset="100%" stopColor="#f5e6a3" stopOpacity="0"/>
                </radialGradient>
                <filter id="dustGlo"><feGaussianBlur stdDeviation="0.4"/></filter>
              </defs>

              {/* a) Central breathing light inside the arch */}
              <ellipse cx="50" cy="55" rx="12" ry="18" fill="url(#portalLight)"
                style={{ animation: 'portalBreathe 5s ease-in-out infinite' }}/>

              {/* d) Golden dust ascending from left crystals */}
              {[
                [33, 80, 0], [30, 82, 1.2], [36, 81, 2.5], [31, 79, 4], [35, 83, 5.5], [28, 81, 3.3],
              ].map(([cx, cy, delay], i) => (
                <circle key={`dl${i}`} cx={cx} cy={cy} r={0.35 + (i % 3) * 0.1}
                  fill="#dbb83d" filter="url(#dustGlo)" opacity="0"
                  style={{ animation: `dustRise ${8 + (i % 3) * 2}s ease-out infinite`, animationDelay: `${delay}s` }}/>
              ))}

              {/* d) Golden dust ascending from right crystals */}
              {[
                [67, 80, 0.8], [70, 82, 2], [64, 81, 3.5], [69, 79, 1.5], [65, 83, 4.8], [72, 81, 6],
              ].map(([cx, cy, delay], i) => (
                <circle key={`dr${i}`} cx={cx} cy={cy} r={0.35 + (i % 3) * 0.1}
                  fill="#dbb83d" filter="url(#dustGlo)" opacity="0"
                  style={{ animation: `dustRise ${8 + (i % 3) * 2}s ease-out infinite`, animationDelay: `${delay}s` }}/>
              ))}
            </svg>
          </div>
        </div>

        {/* "Este es tu umbral" — invitation to scroll */}
        <div className="absolute bottom-10 left-0 right-0 z-10 text-center pointer-events-none">
          <div className="inline-flex items-center gap-4"
            style={{ animation: 'fadeInUp 1.2s ease-out 0.5s both' }}>
            <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="opacity-40"
              style={{ animation: 'chevronPulse 3s ease-in-out infinite' }}>
              <path d="M8 4L8 20" stroke="#c9a227" strokeWidth="1" strokeLinecap="round"/>
              <path d="M2 16L8 22L14 16" stroke="#c9a227" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="8" cy="2" r="1.5" fill="#c9a227" opacity=".5"/>
            </svg>
            <p className="text-[#5c4a2a]/60 text-lg sm:text-xl font-light tracking-wide">
              Este es tu umbral.
            </p>
            <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="opacity-40"
              style={{ animation: 'chevronPulse 3s ease-in-out infinite', animationDelay: '0.3s' }}>
              <path d="M8 4L8 20" stroke="#c9a227" strokeWidth="1" strokeLinecap="round"/>
              <path d="M2 16L8 22L14 16" stroke="#c9a227" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="8" cy="2" r="1.5" fill="#c9a227" opacity=".5"/>
            </svg>
          </div>
        </div>
      </div>

      {/* ═══ CONTENT BELOW — SCROLL TO SEE ═══ */}
      <div className={`transition-opacity duration-[1200ms] ${entering ? 'opacity-0' : 'opacity-100'}`}>
        <div className="max-w-xl mx-auto px-5 py-8">

          {/* Sacred text card */}
          <div
            className="bg-[#5c4a2a]/[.06] border border-[#5c4a2a]/10 rounded-2xl px-6 py-8 sm:px-8 sm:py-10 mb-8"
            style={{ animation: 'fadeInUp 1s ease-out both', animationDelay: '0.3s' }}
          >
            <div className="space-y-4 text-center text-[#5c4a2a]/70 text-sm sm:text-base font-light leading-relaxed italic">
              <p className="text-[#5c4a2a]/90 text-base sm:text-lg font-normal not-italic mb-2">
                Bienvenida, querida alma.
              </p>

              <p>
                Cada vez que llegas aquí, algo en ti se detiene.<br />
                El caos queda fuera. El ruido queda fuera.<br />
                <span className="text-[#c9a227]/80 not-italic">Y la luz origen real te atraviesa.</span>
              </p>

              <div className="flex items-center justify-center gap-2 py-1">
                <div className="w-6 h-px bg-[#c9a227]/30" />
                <div className="w-1 h-1 rounded-full bg-[#c9a227]/40" style={{ animation: 'breatheDot 4s ease-in-out infinite' }} />
                <div className="w-6 h-px bg-[#c9a227]/30" />
              </div>

              <p>
                Has cruzado muchas puertas hoy.<br />
                Esta es diferente.
              </p>

              <p>
                Aquí no se entra con la mente.<br />
                Se entra con el cuerpo, con la respiración,<br />
                con la disposición de no empujar nada.
              </p>

              <p>
                Deja que esta imagen te regule.<br />
                Ya es frecuencia. Ya estás siendo sostenida.
              </p>

              <p className="text-[#5c4a2a]/80 font-normal">
                Permítete llegar. Permítete bajar.<br />
                <span className="text-[#c9a227]/80">El portal hace el resto.</span>
              </p>
            </div>
          </div>

          {/* Question card */}
          <div
            className="bg-white border border-[#c9a227]/20 rounded-2xl px-6 py-8 sm:px-8 shadow-sm"
            style={{ animation: 'fadeInUp 1s ease-out both', animationDelay: '0.7s' }}
          >
            <p className="text-center text-[#5c4a2a]/70 text-xs tracking-wider uppercase mb-4">
              Antes de entrar
            </p>

            <p className="text-center text-[#5c4a2a]/55 text-sm font-light italic mb-5">
              Deja una palabra en tu bitácora...
            </p>

            <p className="text-center text-[#5c4a2a]/90 text-base sm:text-lg font-light leading-relaxed italic mb-6" data-testid="sacred-question">
              {question?.question}
            </p>

            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Escribe desde el sentir..."
              className="w-full bg-[#faf8f4] border border-[#5c4a2a]/10 rounded-xl px-5 py-4 text-[#5c4a2a]/70 text-sm placeholder:text-[#5c4a2a]/25 focus:outline-none focus:border-[#c9a227]/30 focus:ring-1 focus:ring-[#c9a227]/15 resize-none transition-all leading-relaxed"
              rows={3}
              data-testid="umbral-response-input"
            />

            <p className="text-[#5c4a2a]/50 text-xs mt-2 mb-6 text-center font-light leading-relaxed">
              Lo que escribas se guarda en tu Bitácora personal.<br />
              Un espacio sagrado para releer y tomar consciencia de tu camino.
            </p>

            <button
              onClick={handleSubmit}
              disabled={!response.trim() || saving}
              className="w-full py-3.5 bg-[#c9a227] text-white text-sm tracking-[0.2em] uppercase rounded-xl hover:bg-[#b8922a] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              data-testid="umbral-enter-btn"
            >
              {saving ? 'Guardando...' : 'Cruzar el umbral'}
            </button>

            <button
              onClick={handleSkip}
              className="mt-4 w-full text-[#5c4a2a]/45 text-xs hover:text-[#5c4a2a]/65 transition-colors font-light"
              data-testid="umbral-skip-btn"
            >
              Hoy solo quiero estar
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CampusUmbral;
